import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ALL_SUBJECTS, BRANCHES } from "@/lib/constants";
import OpticalSheet from "@/components/OpticalSheet";
import {
  Loader2, Plus, Trash2, ScanLine, Upload, Send,
  ChevronDown, ChevronUp, Edit3, BookOpen, Layers, Sparkles, Printer
} from "lucide-react";
import { FileDown } from "lucide-react";
import { printResultDocuments } from "@/lib/resultDocument";

const PRESETS = {
  TYT: [
    ["Türkçe", 40],
    ["Tarih", 5],
    ["Coğrafya", 5],
    ["Felsefe", 5],
    ["Din Kültürü", 5],
    ["Matematik", 30],
    ["Geometri", 10],
    ["Fizik", 7],
    ["Kimya", 7],
    ["Biyoloji", 6],
  ],
  AYT: [
    ["Matematik", 40],
    ["Fizik", 14],
    ["Kimya", 13],
    ["Biyoloji", 13],
    ["Türk Dili ve Edebiyatı", 24],
    ["Tarih-1", 10],
    ["Coğrafya-1", 6],
    ["Tarih-2", 11],
    ["Coğrafya-2", 11],
    ["Felsefe Grubu", 12],
    ["Din Kültürü", 6],
  ],
  BRANS: [
    ["Türkçe", 40],
  ],
};

const mkSub = (name = "Türkçe", count = 40) => ({
  name,
  question_count: count,
  answer_key: "",
  kazanimlar: [],
  showKaz: false,
  showOptik: false, // interactive bubble sheet toggle
});

export default function OpticalExams() {
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examTypeMode, setExamTypeMode] = useState("TYT"); // TYT | AYT | BRANS
  const [form, setForm] = useState({ name: "", exam_type: "TYT", date: "" });
  const [bulkCount, setBulkCount] = useState(1);
  const [subjects, setSubjects] = useState(PRESETS.TYT.map(([n, c]) => mkSub(n, c)));
  const [saving, setSaving] = useState(false);

  // Result publishing state
  const [examId, setExamId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [answers, setAnswers] = useState({});
  const [activeOptikSubject, setActiveOptikSubject] = useState("");
  const [pubBusy, setPubBusy] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [tracksSel, setTracksSel] = useState([]); // çoklu sınıf/alan filtresi (en fazla 3)
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const markRef = useRef(null);

  // Toplu/optik: öğrenci seçilince doğrudan işaretleme (öğrenci) sayfasına kaydır
  useEffect(() => {
    if (studentId && examId && markRef.current) {
      const t = setTimeout(() => markRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      return () => clearTimeout(t);
    }
  }, [studentId, examId]);
  const [editExamId, setEditExamId] = useState(null);
  const [classReport, setClassReport] = useState(null);
  const [assignTracks, setAssignTracks] = useState([]); // sınava atanan sınıf/alan (en fazla 3)
  const [assignStudents, setAssignStudents] = useState([]); // sınava atanan öğrenciler (çoklu)
  const [showAssign, setShowAssign] = useState(false);

  const toggleAssignTrack = (t) =>
    setAssignTracks((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return prev;
      }
      return [...prev, t];
    });

  const toggleAssignStudent = (id) =>
    setAssignStudents((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleTrack = (t) =>
    setTracksSel((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return prev;
      }
      return [...prev, t];
    });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s, m, t] = await Promise.all([api.get("/optical-exams"), api.get("/students"), api.get("/meta"), api.get("/optical-templates")]);
      setExams(e.data);
      setStudents(s.data);
      setTracks(m.data.tracks || []);
      setTemplates(t.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (mode) => {
    setExamTypeMode(mode);
    const apiType = mode === "BRANS" ? "Branş Deneme" : mode;
    setForm((f) => ({ ...f, exam_type: apiType }));
    if (mode === "BRANS") {
      setSubjects([mkSub("Türkçe", 40)]);
    } else {
      setSubjects(PRESETS[mode].map(([n, c]) => mkSub(n, c)));
    }
  };

  const setSub = (i, f, v) =>
    setSubjects((ss) =>
      ss.map((s, j) => {
        if (j !== i) return s;
        const updated = { ...s, [f]: v };
        // If question count changes, adjust answer_key length or kazanimlar
        if (f === "question_count") {
          const count = Number(v) || 0;
          if (updated.kazanimlar.length < count) {
            updated.kazanimlar = [...updated.kazanimlar, ...Array(count - updated.kazanimlar.length).fill("")];
          }
        }
        return updated;
      })
    );

  const setKaz = (i, qi, v) =>
    setSubjects((ss) =>
      ss.map((s, j) => {
        if (j !== i) return s;
        const k = [...s.kazanimlar];
        k[qi] = v;
        return { ...s, kazanimlar: k };
      })
    );

  const saveExam = async () => {
    if (!form.name || !form.date) return toast.error("Sınav adı ve tarihi zorunlu");
    const incomplete = [];
    for (const s of subjects) {
      const qc = Number(s.question_count) || 0;
      const keyLen = s.answer_key.replace(/\s/g, "").length;
      if (!s.name || qc <= 0) {
        return toast.error("Ders adı ve geçerli soru sayısı girin");
      }
      if (keyLen !== qc) incomplete.push(s.name);
    }
    if (incomplete.length && !window.confirm(`"${incomplete.join(", ")}" dersinin cevap anahtarı eksik. Anahtarı sonradan listeden (kalem) girebilirsiniz — girince mevcut sonuçlar otomatik yeniden hesaplanır. Yine de kaydedilsin mi?`)) return;
    setSaving(true);
    try {
      const count = editExamId ? 1 : Math.min(100, Math.max(1, Number(bulkCount) || 1));
      const payloadSubjects = subjects.map(({ showKaz, showOptik, ...s }) => ({
        ...s,
        question_count: Number(s.question_count),
        answer_key: s.answer_key.toUpperCase().replace(/\s/g, ""),
      }));
      if (editExamId) {
        await api.put(`/optical-exams/${editExamId}`, { name: form.name, date: form.date, subjects: payloadSubjects, assigned_tracks: assignTracks, assigned_student_ids: assignStudents });
        toast.success("Sınav güncellendi — cevap anahtarları tamamsa mevcut sonuçlar yeniden hesaplandı");
        setEditExamId(null);
      } else {
        for (let i = 0; i < count; i++) {
          const name = count > 1 ? `${form.name.trim()} ${i + 1}` : form.name;
          let date = form.date;
          if (count > 1 && form.date) {
            const d = new Date(form.date + "T00:00:00");
            d.setDate(d.getDate() + i);
            date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          }
          await api.post("/optical-exams", { ...form, name, date, subjects: payloadSubjects, assigned_tracks: assignTracks, assigned_student_ids: assignStudents });
        }
        toast.success(count > 1 ? `${count} deneme oluşturuldu — art arda günlere sıralandı` : "Optik sınav ve cevap anahtarı kaydedildi");
      }
      setForm({ name: "", exam_type: form.exam_type, date: "" });
      setBulkCount(1);
      setAssignTracks([]);
      setAssignStudents([]);
      setShowAssign(false);
      setSubjects(PRESETS[examTypeMode === "BRANS" ? "BRANS" : examTypeMode].map(([n, c]) => mkSub(n, c)));
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startExamEdit = (x) => {
    setEditExamId(x.id);
    setExamTypeMode(x.exam_type === "Branş Deneme" ? "BRANS" : x.exam_type === "AYT" ? "AYT" : "TYT");
    setForm({ name: x.name, exam_type: x.exam_type, date: x.date || "" });
    setAssignTracks(x.assigned_tracks || []);
    setAssignStudents(x.assigned_student_ids || []);
    setSubjects(
      x.subjects.map((s) => ({
        name: s.name,
        question_count: s.question_count,
        answer_key: s.answer_key || "",
        kazanimlar: s.kazanimlar || [],
        showKaz: false,
        showOptik: false,
      }))
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) return toast.error("Şablon adı girin");
    for (const s of subjects) {
      const qc = Number(s.question_count) || 0;
      if (!s.name || qc <= 0 || s.answer_key.replace(/\s/g, "").length !== qc)
        return toast.error("Şablon için önce tüm derslerin cevap anahtarını tamamlayın");
    }
    try {
      await api.post("/optical-templates", {
        name: tplName.trim(),
        exam_type: form.exam_type,
        subjects: subjects.map(({ showKaz, showOptik, ...s }) => ({
          ...s,
          question_count: Number(s.question_count),
          answer_key: s.answer_key.toUpperCase().replace(/\s/g, ""),
        })),
      });
      toast.success("Şablon kaydedildi");
      setTplName("");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const applyTemplate = (tpl) => {
    const mode = tpl.exam_type === "Branş Deneme" ? "BRANS" : tpl.exam_type === "AYT" ? "AYT" : "TYT";
    setExamTypeMode(mode);
    setForm((f) => ({ ...f, exam_type: tpl.exam_type }));
    setSubjects(
      tpl.subjects.map((s) => ({
        name: s.name,
        question_count: s.question_count,
        answer_key: s.answer_key,
        kazanimlar: s.kazanimlar || [],
        showKaz: false,
        showOptik: false,
      }))
    );
    toast.success(`"${tpl.name}" şablonu yüklendi — dersler ve cevap anahtarları geldi`);
  };

  const openClassReport = async (eid) => {
    setClassReport({ loading: true, rows: [], exam: null });
    try {
      const { data } = await api.get(`/optical-exams/${eid}/class-report`);
      setClassReport({ loading: false, ...data });
    } catch (e) {
      toast.error(apiErr(e));
      setClassReport(null);
    }
  };

  const printClassReport = () => {
    if (!classReport || classReport.loading || !classReport.exam) return;
    const { exam, rows } = classReport;
    const trs = rows
      .map(
        (r) => `<tr><td>${r.rank}</td><td style="text-align:left">${r.name}</td><td>${r.track || "-"}</td>
      <td>${r.totals?.dc ?? 0}</td><td>${r.totals?.yc ?? 0}</td><td>${r.totals?.blank ?? 0}</td>
      <td><b>${Number(r.totals?.net || 0).toFixed(2)}</b></td></tr>`
      )
      .join("");
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${exam.name} — Sınıf Sonuç Raporu</title>
      <style>body{font-family:Arial,sans-serif;padding:24px}h1{font-size:18px}p{color:#555;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ccc;padding:6px 8px;font-size:12px;text-align:center}
      th{background:#1e1b4b;color:#fff}</style></head><body>
      <h1>${exam.name} — Sınıf Sonuç Raporu</h1><p>${exam.exam_type} · ${exam.date} · ${rows.length} öğrenci</p>
      <table><thead><tr><th>Sıra</th><th style="text-align:left">Öğrenci</th><th>Alan</th><th>D</th><th>Y</th><th>B</th><th>Net</th></tr></thead>
      <tbody>${trs}</tbody></table>
      <script>window.onload=function(){window.print()}<\/script></body></html>`);
    w.document.close();
  };

  const publish = async () => {
    if (!examId || !studentId) return toast.error("Sınav ve öğrenci seçin");
    setPubBusy(true);
    try {
      const { data } = await api.post(`/optical-exams/${examId}/results`, { student_id: studentId, answers });
      toast.success(`Sonuç yayınlandı — Toplam Net: ${data.net}. Öğrenci ve veliye bildirim iletildi.`);
      // Seri işaretleme: listedeki bir sonraki öğrenciye otomatik geç
      const idx = filteredStudents.findIndex((s) => s.id === studentId);
      const next = idx >= 0 ? filteredStudents[idx + 1] : null;
      setAnswers({});
      setActiveOptikSubject("");
      setStudentId(next ? next.id : "");
      if (next) toast.info(`Sonraki öğrenci hazır: ${next.name}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPubBusy(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !examId) return;
    const fd = new FormData();
    fd.append("file", file);
    setFileBusy(true);
    try {
      const { data } = await api.post(`/optical-exams/${examId}/results-file`, fd);
      const fb = data.fallback?.length ? ` · Atanan öğrenciye düşen: ${data.fallback.length}` : "";
      toast.success(`${data.created} öğrenci sonucu yüklendi${fb}${data.skipped.length ? ` · Bulunamayan: ${data.skipped.join(", ")}` : ""}`);
      load();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setFileBusy(false);
    }
  };

  const selExam = exams.find((x) => x.id === examId);
  const filteredStudents = students.filter((s) => {
    if (selExam?.assigned_student_ids?.length && !selExam.assigned_student_ids.includes(s.id)) return false;
    if (tracksSel.length && !tracksSel.includes(s.track)) return false;
    return true;
  });
  const studentsForAssign = assignTracks.length ? students.filter((s) => assignTracks.includes(s.track)) : students;

  return (
    <div className="space-y-6" data-testid="optical-panel">
      {/* Page Title */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
        <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
          <ScanLine size={14} className="text-indigo-600" /> Dijital Optik & Canlı Sınav
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1">
          Canlı Sınav (Optik Okuma & İşaretleme)
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          TYT, AYT veya Branş denemeleri için <b>5 şıklı (A, B, C, D, E) interaktif optik form</b> ile cevap anahtarı tanımlayın. Öğrenci optiklerini ekrandan tıklayarak işaretleyin veya sekonik/optik okuyucu dosyasıyla yükleyin.
        </p>
        </div>
        {exams.some((x) => (x.subjects || []).some((s) => /sosyal|fen|temel matematik/i.test(s.name))) && (
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm("Eski 4 dersli TÜM TYT sınavları alt branşlara bölünecek (Tarih/Coğrafya/Felsefe/Din, Fizik/Kimya/Biyoloji, Matematik/Geometri) ve tüm sonuçlar yeniden hesaplanacak. Devam?")) return;
              try {
                const { data } = await api.post("/optical-exams/split-all");
                toast.success(`${data.exams} sınav, ${data.updated} sonuç alt branşlara bölündü`);
                load();
              } catch (e) { toast.error(apiErr(e)); }
            }}
            className="text-xs font-bold px-3 py-2 rounded-full border bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 transition-colors"
            data-testid="opt-split-all-btn"
          >
            Tümünü Alt Branşlara Böl
          </button>
        )}
      </div>

      {/* SECTION 1: Create Exam & Optical Answer Key */}
      <div className="card-ta p-5 space-y-4" data-testid="opt-builder">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-indigo-900 text-white text-xs font-bold flex items-center justify-center">1</span>
            <h2 className="font-display font-bold text-slate-900 text-base">Optik Sınav & Cevap Anahtarı Tanımla</h2>
          </div>

          {/* Mode Selector: TYT | AYT | BRANŞ */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="opt-mode-tabs">
            {[
              ["TYT", "TYT (Tüm Dersler)"],
              ["AYT", "AYT (Tüm Dersler)"],
              ["BRANS", "Branş Denemesi"],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => applyPreset(mode)}
                data-testid={`opt-preset-${mode}`}
                className={`px-3.5 py-1.5 text-xs font-bold transition-colors ${
                  examTypeMode === mode
                    ? "bg-indigo-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Saved Templates (Şablon Kaydet / Yükle) */}
        <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="opt-templates">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
            <Sparkles size={13} /> Şablonlar:
          </span>
          {templates.length === 0 && <span className="text-xs text-slate-400">Kayıtlı şablon yok</span>}
          {templates.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-indigo-200 bg-white pl-2.5 pr-1 py-1">
              <button type="button" onClick={() => applyTemplate(t)} className="text-indigo-700 hover:text-indigo-900" data-testid={`opt-tpl-apply-${t.id}`}>
                {t.name}
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.delete(`/optical-templates/${t.id}`);
                    load();
                  } catch (e) {
                    toast.error(apiErr(e));
                  }
                }}
                className="text-slate-300 hover:text-rose-600 px-1"
                title="Şablonu sil"
                data-testid={`opt-tpl-del-${t.id}`}
              >
                ×
              </button>
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 ml-auto">
            <input
              className="ta-input !py-1 !text-xs w-44"
              placeholder="Şablon adı... (örn: Haftalık TYT Seti)"
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              data-testid="opt-tpl-name"
            />
            <Button type="button" size="sm" variant="outline" onClick={saveTemplate} className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="opt-tpl-save">
              <Sparkles size={13} />
              <span className="ml-1">Şablon Kaydet</span>
            </Button>
          </span>
        </div>

        {/* Exam Info */}
        <div className="grid grid-cols-1 sm:grid-cols-[1.5fr_0.9fr_1.1fr_0.6fr] gap-3">
          <div>
            <label className="ta-label">Sınav Adı</label>
            <input
              className="ta-input font-medium"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={
                examTypeMode === "BRANS"
                  ? "Örn: 11. Sınıf Matematik Branş Denemesi 3"
                  : "Örn: TYT Genel Deneme Sınavı 1 (Özdebir / Bilgi Sarmal)"
              }
              data-testid="opt-name"
            />
          </div>
          <div>
            <label className="ta-label">Sınav Tarihi</label>
            <input
              type="date"
              className="ta-input"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="opt-date"
            />
          </div>
          <div>
            <label className="ta-label">Sınıf / Öğrenci Seç (ops.)</label>
            <button
              type="button"
              onClick={() => setShowAssign(!showAssign)}
              className="ta-input text-left text-sm flex items-center justify-between gap-1"
              data-testid="opt-assign-toggle"
            >
              <span className={`truncate ${assignTracks.length || assignStudents.length ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                {assignTracks.length || assignStudents.length ? `${assignTracks.length} alan · ${assignStudents.length} öğrenci` : "Sınıf / Öğrenci Seç"}
              </span>
              <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${showAssign ? "rotate-180" : ""}`} />
            </button>
          </div>
          <div>
            <label className="ta-label">Adet</label>
            <input
              type="number"
              min="1"
              max="100"
              className="ta-input text-center font-bold"
              value={bulkCount}
              onChange={(e) => setBulkCount(e.target.value)}
              title="1'den fazla girerseniz denemeler numaralanır ve girilen tarihten başlayarak art arda günlere sıralanır"
              data-testid="opt-bulk-count"
            />
          </div>
        </div>
        {showAssign && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3" data-testid="opt-assign-panel">
            <div>
              <div className="text-xs font-bold text-slate-500 mb-1.5">SINIF / ALAN (en fazla 3)</div>
              <div className="flex flex-wrap gap-1.5">
                {tracks.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleAssignTrack(t)}
                    data-testid={`opt-assign-track-${t}`}
                    className={`text-xs px-2.5 py-1.5 rounded-full border font-semibold transition-colors ${
                      assignTracks.includes(t) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-slate-500 mb-1.5">
                ÖĞRENCİLER (çoklu — boş bırakılırsa seçili alanların tümü)
              </div>
              <div className="max-h-36 overflow-y-auto grid sm:grid-cols-2 gap-1 bg-white border border-slate-200 rounded-lg p-2">
                {studentsForAssign.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`opt-assign-stu-${s.id}`}>
                    <input
                      type="checkbox"
                      checked={assignStudents.includes(s.id)}
                      onChange={() => toggleAssignStudent(s.id)}
                      className="accent-emerald-600"
                    />
                    {s.name} {s.track ? <span className="text-slate-400">· {s.track}</span> : null}
                  </label>
                ))}
                {studentsForAssign.length === 0 && <div className="text-xs text-slate-400 px-2 py-1">Öğrenci yok</div>}
              </div>
            </div>
            <p className="text-[11px] text-slate-400">
              Bu sınava atanan sınıf/öğrenciler, aşağıdaki "Sonuç Yayınla" bölümünde sınav seçildiğinde otomatik süzülür.
            </p>
          </div>
        )}
        {Number(bulkCount) > 1 && form.date && (
          <div className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2" data-testid="opt-bulk-info">
            {bulkCount} deneme oluşturulacak: "{form.name || "Deneme"} 1" ({form.date}) → "{form.name || "Deneme"} {bulkCount}" (art arda günlerde)
          </div>
        )}

        {/* Subjects & Optical Sheets */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Dersler, Soru Sayıları & 5 Şıklı Optik Cevap Anahtarları ({subjects.length} Ders)
            </span>
            <span className="text-xs text-slate-500">
              Toplam Soru: <b>{subjects.reduce((a, s) => a + (Number(s.question_count) || 0), 0)}</b>
            </span>
          </div>

          {subjects.map((s, i) => {
            const qc = Number(s.question_count) || 0;
            const keyClean = s.answer_key.replace(/\s/g, "");
            const isKeyComplete = keyClean.length === qc && qc > 0;

            return (
              <div
                key={i}
                className="rounded-2xl border border-slate-200/90 bg-slate-50/50 p-4 space-y-3 transition-shadow hover:shadow-sm"
                data-testid={`opt-subject-${i}`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_110px_2fr_auto_auto] gap-2.5 items-center">
                  {/* Subject Name */}
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Ders Adı</label>
                    <input
                      className="ta-input !py-1.5 font-semibold text-sm"
                      list="opt-subjects"
                      value={s.name}
                      onChange={(e) => setSub(i, "name", e.target.value)}
                      placeholder="Ders adı"
                      data-testid={`opt-subject-name-${i}`}
                    />
                    <datalist id="opt-subjects">
                      {ALL_SUBJECTS.map((x) => (
                        <option key={x} value={x} />
                      ))}
                    </datalist>
                  </div>

                  {/* Question Count (Auto default, manual override) */}
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Soru Sayısı</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="ta-input !py-1.5 text-center font-bold text-sm"
                      value={s.question_count}
                      onChange={(e) => setSub(i, "question_count", e.target.value)}
                      title="Soru sayısını elle değiştirebilirsiniz"
                      data-testid={`opt-subject-count-${i}`}
                    />
                  </div>

                  {/* Answer Key String input + Quick Info */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Cevap Anahtarı ({qc} Harf)</label>
                      <span
                        className={`text-[10px] font-extrabold px-1.5 py-0.2 rounded ${
                          isKeyComplete
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {keyClean.length} / {qc}
                      </span>
                    </div>
                    <input
                      className="ta-input !py-1.5 font-mono text-xs uppercase tracking-widest font-bold"
                      value={s.answer_key}
                      onChange={(e) => setSub(i, "answer_key", e.target.value.toUpperCase())}
                      placeholder={`Örn: ${"A".repeat(Math.min(5, qc))}...`}
                      data-testid={`opt-subject-key-${i}`}
                    />
                  </div>

                  {/* Button to toggle 5-option interactive optical bubble sheet */}
                  <div className="flex flex-col items-center">
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Optik</label>
                    <Button
                      type="button"
                      size="sm"
                      variant={s.showOptik ? "default" : "outline"}
                      onClick={() => setSub(i, "showOptik", !s.showOptik)}
                      className={`h-9 px-3 text-xs font-bold ${
                        s.showOptik
                          ? "bg-indigo-900 text-white"
                          : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      }`}
                      data-testid={`opt-toggle-sheet-${i}`}
                    >
                      <Edit3 size={13} className="mr-1" />
                      {s.showOptik ? "Optiği Kapat" : "Optikten İşaretle"}
                    </Button>
                  </div>

                  {/* Action buttons (Kazanım toggle + Delete) */}
                  <div className="flex items-center gap-1 pt-4">
                    <button
                      type="button"
                      onClick={() => setSub(i, "showKaz", !s.showKaz)}
                      className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-indigo-700"
                      title="Soru kazanımlarını aç/kapat"
                      data-testid={`opt-subject-kaz-toggle-${i}`}
                    >
                      {s.showKaz ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {subjects.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setSubjects((ss) => ss.filter((_, j) => j !== i))}
                        className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        title="Dersi kaldır"
                        data-testid={`opt-subject-del-${i}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Interactive Optical Bubble Sheet for this Subject (5 şıklı optik form) */}
                {s.showOptik && (
                  <div className="pt-2">
                    <OpticalSheet
                      title={`${s.name || "Ders"} — 5 Şıklı Optik Cevap Anahtarı`}
                      questionCount={qc}
                      value={s.answer_key}
                      onChange={(newVal) => setSub(i, "answer_key", newVal)}
                      kazanimlar={s.kazanimlar}
                    />
                  </div>
                )}

                {/* Learning Outcomes (Kazanımlar) Input Grid */}
                {s.showKaz && (
                  <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-2">
                    <div className="text-xs font-bold text-slate-700 flex items-center justify-between">
                      <span>Soru Bazında Kazanımlar ({s.name})</span>
                      <span className="text-[11px] text-slate-400 font-normal">Yanlış yapılan sorular sonuç belgesinde bu kazanımla listelenir</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5 max-h-48 overflow-y-auto p-1">
                      {Array.from({ length: qc }).map((_, qi) => (
                        <div key={qi} className="relative">
                          <span className="absolute left-1.5 top-1.5 text-[9px] font-mono text-slate-400 font-bold">
                            S{qi + 1}
                          </span>
                          <input
                            className="ta-input !py-1 !pl-6 !pr-1 text-[11px]"
                            value={s.kazanimlar[qi] || ""}
                            onChange={(e) => setKaz(i, qi, e.target.value)}
                            placeholder="Kazanım"
                            data-testid={`opt-subject-kaz-${i}-${qi}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Actions for Builder */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => setSubjects((ss) => [...ss, mkSub("Yeni Ders", 20)])}
            className="border-slate-300 text-slate-700"
            data-testid="opt-add-subject"
          >
            <Plus size={15} /> <span className="ml-1">Ekstra Ders Ekle</span>
          </Button>

          <Button
            type="button"
            onClick={saveExam}
            disabled={saving}
            className="bg-indigo-900 hover:bg-indigo-800 h-11 px-6 text-sm font-bold shadow-md"
            data-testid="opt-save"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
            <span className="ml-1.5">{editExamId ? "Sınavı Güncelle (Anahtar sonradan da girilebilir)" : Number(bulkCount) > 1 ? `${bulkCount} Denemeyi Oluştur` : "Sınavı & Cevap Anahtarını Kaydet"}</span>
          </Button>
        </div>
      </div>

      {/* SECTION 2: Mark Student Answers (Elle Optik İşaretleme) */}
      <div className="card-ta p-5 space-y-4" data-testid="opt-publish">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
          <span className="w-6 h-6 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">2</span>
          <div>
            <h2 className="font-display font-bold text-slate-900 text-base">Sonuç Yayınla (İnteraktif Optik İşaretleme)</h2>
            <p className="text-xs text-slate-500">Öğrenciyi seçin, optik form üzerinden öğrencinin cevaplarını tıklayarak işaretleyin.</p>
          </div>
        </div>

        <div>
          <label className="ta-label">Sınıf / Alan Filtresi (ops. — en fazla 3, çoklu seçilebilir)</label>
          <div className="flex flex-wrap gap-1.5" data-testid="opt-track-chips">
            {tracks.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTrack(t)}
                data-testid={`opt-track-chip-${t}`}
                className={`text-xs px-2.5 py-1.5 rounded-full border font-semibold transition-colors ${
                  tracksSel.includes(t) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="ta-label">1. Sınav Seç</label>
            <select
              className="ta-input"
              value={examId}
              onChange={(e) => {
                const id = e.target.value;
                setExamId(id);
                setAnswers({});
                setActiveOptikSubject("");
                const ex = exams.find((x) => x.id === id);
                if (ex?.assigned_tracks?.length) setTracksSel(ex.assigned_tracks);
              }}
              data-testid="opt-exam-select"
            >
              <option value="">Sınav seçiniz...</option>
              {exams.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} · {x.exam_type} · {x.date} ({x.subjects?.length} ders)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="ta-label">2. Öğrenci Seç ({filteredStudents.length} öğrenci)</label>
            <select
              className="ta-input"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              data-testid="opt-student-select"
            >
              <option value="">Öğrenci seçiniz...</option>
              {filteredStudents.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.track ? `· ${s.track}` : ""} ({s.email || s.username || ""})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Selected Exam Marking Area */}
        {selExam && studentId && (
          <div ref={markRef} className="space-y-4 pt-2 border-t border-slate-100 scroll-mt-4" data-testid="opt-marking-area">
            {/* Subject Tabs */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Öğrenci Optik Cevapları — Dersi Seçip İşaretleyin:
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {selExam.subjects.map((s) => {
                const ansStr = answers[s.name] || "";
                const markedCount = ansStr.replace(/\s/g, "").length;
                const isCurrent = (activeOptikSubject || selExam.subjects[0]?.name) === s.name;

                return (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setActiveOptikSubject(s.name)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all flex items-center gap-2 ${
                      isCurrent
                        ? "bg-indigo-900 text-white border-indigo-900 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <span>{s.name}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded font-mono ${
                        isCurrent
                          ? "bg-white/20 text-white"
                          : markedCount > 0
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {markedCount}/{s.question_count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Active Subject's 5-option Optical Bubble Sheet */}
            {(() => {
              const curSubName = activeOptikSubject || selExam.subjects[0]?.name;
              const curSub = selExam.subjects.find((s) => s.name === curSubName);
              if (!curSub) return null;

              return (
                <div className="space-y-3">
                  <OpticalSheet
                    title={`${selExam.name} — ${curSub.name} Optik Formu`}
                    questionCount={curSub.question_count}
                    value={answers[curSub.name] || ""}
                    onChange={(newVal) =>
                      setAnswers((prev) => ({ ...prev, [curSub.name]: newVal }))
                    }
                    kazanimlar={curSub.kazanimlar}
                  />

                  {/* Raw text input for quick copy-paste fallback */}
                  <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                    <span className="text-xs font-semibold text-slate-600 shrink-0">
                      Metin Olarak Dize:
                    </span>
                    <input
                      className="ta-input !py-1 font-mono text-xs uppercase tracking-widest flex-1"
                      value={answers[curSub.name] || ""}
                      onChange={(e) =>
                        setAnswers((prev) => ({
                          ...prev,
                          [curSub.name]: e.target.value.toUpperCase(),
                        }))
                      }
                      placeholder={`Örn: A B C D E... (boş sorular için boşluk)`}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Publish Button */}
            <div className="pt-2">
              <Button
                type="button"
                onClick={publish}
                disabled={pubBusy}
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 h-12 px-8 text-sm font-bold shadow-md"
                data-testid="opt-publish-btn"
              >
                {pubBusy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                <span className="ml-2">Öğrenci Sonucunu Değerlendir & Yayınla</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* SECTION 3: Bulk Upload via File (Sekonik / Optical Reader File) */}
      <div className="card-ta p-5 space-y-3" data-testid="opt-file">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
          <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center">3</span>
          <div>
            <h2 className="font-display font-bold text-slate-900 text-base">Optik Okuyucu / Sekonik Dosyasıyla Toplu Yükleme</h2>
            <p className="text-xs text-slate-500">
              Kurum optik okuma makinenizden çıkan TXT veya CSV dosyasını seçerek tüm sınıfın sonuçlarını tek tıkla yükleyin.
            </p>
          </div>
        </div>

        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-1">
          <div className="font-bold text-slate-800">Dosya Formatı:</div>
          <div>
            Her satır: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-300 font-mono text-indigo-700">kullanici_adi_veya_ad_soyad;Ders1_Cevaplari;Ders2_Cevaplari...</code>
          </div>
          <div className="text-slate-400 text-[11px]">
            Ders sırası yukarıda tanımladığınız sınavın ders sırasıyla aynı olmalıdır. Sütun sayısı ders sayısından farklıysa (ör. 4 blok veya tek dize) cevaplar birleştirilip derslerin soru sayısına göre sırayla bölünür. Boş cevaplar için boşluk bırakın; boşluklar silinmez. Sonuçlar anında yayınlanır; PPD, Branş ve Genel Deneme panellerine otomatik düşer.
          </div>
          <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5">
            Satırın ilk alanı öğrenciyle eşleşmezse, satır sınavın "Sınıf / Öğrenci Seç" ile atandığı öğrencilere (veya atanan sınıfların öğrencilerine) sırayla otomatik atanır.
          </div>
        </div>

        <div className="pt-1">
          <label
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-bold transition-all ${
              examId
                ? "bg-white border-indigo-300 text-indigo-700 hover:bg-indigo-50 cursor-pointer shadow-sm"
                : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
            }`}
          >
            {fileBusy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            <span>Optik Dosyası Seç (.txt / .csv / .dat)</span>
            <input
              type="file"
              accept=".txt,.csv,.dat"
              onChange={uploadFile}
              className="hidden"
              disabled={!examId}
              data-testid="opt-file-input"
            />
          </label>
          {!examId && (
            <span className="ml-3 text-xs text-amber-600 font-semibold">
              * Dosya yüklemek için önce yukarıdan sınav seçin
            </span>
          )}
        </div>
      </div>

      {/* SECTION 4: List of Defined Optical Exams */}
      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="animate-spin inline" size={24} />
        </div>
      ) : exams.length > 0 ? (
        <div className="card-ta p-5 space-y-3" data-testid="opt-exam-list">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Tanımlı Optik Sınavlar ({exams.length})
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exams.map((x) => (
              <div
                key={x.id}
                className="rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-3 bg-white hover:border-slate-300 transition-colors"
                data-testid={`opt-exam-item-${x.id}`}
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${
                        x.exam_type?.includes("AYT")
                          ? "bg-amber-100 text-amber-700 border-amber-200"
                          : x.exam_type?.includes("Branş")
                          ? "bg-purple-100 text-purple-700 border-purple-200"
                          : "bg-blue-100 text-blue-700 border-blue-200"
                      }`}
                    >
                      {x.exam_type}
                    </span>
                    <span className="font-display font-bold text-slate-900 truncate">
                      {x.name}
                    </span>
                  </div>

                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>{x.date}</span>
                    <span>·</span>
                    <span>{x.subjects?.length} ders</span>
                    <span>·</span>
                    <span>{x.subjects?.reduce((a, s) => a + (Number(s.question_count) || 0), 0)} soru</span>
                    <span>·</span>
                    <span className="font-bold text-emerald-700">{x.result_count} Sonuç</span>
                    <span>·</span>
                    <span className={`font-bold ${x.approved_count === x.result_count && x.result_count > 0 ? "text-indigo-700" : "text-amber-600"}`} data-testid={`opt-exam-approved-${x.id}`}>{x.approved_count || 0} Belge Onaylı</span>
                    {x.subjects?.some((s) => !s.answer_key) && (
                      <span className="font-bold text-amber-600">· Cevap Anahtarı Eksik (kalemden girin)</span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    if (!x.result_count) return toast.error("Bu sınav için henüz sonuç yok");
                    if (!window.confirm(`"${x.name}" sınavının ${x.result_count} sonucu onaylansın ve her öğrenci için türüne göre (TYT / AYT Sayısal / EA / Sözel) sonuç belgesi oluşturulsun mu?`)) return;
                    try {
                      const { data } = await api.put(`/optical-exams/${x.id}/approve`, {});
                      toast.success(`${data.updated} sonuç onaylandı — belgeler oluşturuldu, öğrenci ve velilere bildirildi`);
                      load();
                    } catch (e) {
                      toast.error(apiErr(e));
                    }
                  }}
                  className={`text-xs font-bold px-2.5 py-1 rounded-full border shrink-0 transition-colors ${x.approved_count === x.result_count && x.result_count > 0 ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"}`}
                  title="Tüm sonuçları onayla ve türüne göre sonuç belgesi oluştur"
                  data-testid={`opt-exam-approve-${x.id}`}
                >
                  {x.approved_count === x.result_count && x.result_count > 0 ? "Belgeler Hazır" : "Onayla & Belge Oluştur"}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/optical-exams/${x.id}/recalculate`);
                      toast.success(`${data.updated} sonucun netleri yeniden hesaplandı`);
                      load();
                    } catch (e) {
                      toast.error(apiErr(e));
                    }
                  }}
                  className="text-xs font-bold px-2.5 py-1 rounded-full border shrink-0 bg-white text-slate-700 border-slate-300 hover:bg-slate-50 transition-colors"
                  title="Tüm öğrenci sonuçlarının D/Y/B ve netlerini cevap anahtarına göre yeniden hesapla"
                  data-testid={`opt-exam-recalc-${x.id}`}
                >
                  Netleri Yeniden Hesapla
                </button>
                {(x.subjects || []).some((s) => /sosyal|fen|temel matematik/i.test(s.name)) && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm("Sosyal → Tarih/Coğrafya/Felsefe/Din, Fen → Fizik/Kimya/Biyoloji, Temel Matematik → Matematik/Geometri olarak bölünecek ve tüm sonuçlar yeniden hesaplanacak. Devam?")) return;
                      try {
                        const { data } = await api.post(`/optical-exams/${x.id}/split-subjects`);
                        toast.success(`${data.updated} sonuç alt branşlara bölündü`);
                        load();
                      } catch (e) {
                        toast.error(apiErr(e));
                      }
                    }}
                    className="text-xs font-bold px-2.5 py-1 rounded-full border shrink-0 bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 transition-colors"
                    title="Eski 4 dersli TYT sınavını alt branşlara (Tarih, Coğrafya, Fizik...) böl"
                    data-testid={`opt-exam-split-${x.id}`}
                  >
                    Alt Branşlara Böl
                  </button>
                )}

                <button
                  type="button"
                  onClick={async () => {
                    if (!x.approved_count) return toast.error("Onaylı belge yok — önce sonuçları onaylayın");
                    try {
                      const { data } = await api.get(`/optical-exams/${x.id}/documents`);
                      const ok = await printResultDocuments(data, x.name);
                      if (!ok) toast.error("Belge penceresi açılamadı. Pop-up iznini kontrol edin.");
                    } catch (e) {
                      toast.error(apiErr(e));
                    }
                  }}
                  className="text-xs font-bold px-2.5 py-1 rounded-full border shrink-0 bg-white text-slate-700 border-slate-300 hover:bg-slate-50 transition-colors flex items-center gap-1"
                  title="Tüm onaylı belgeleri tek PDF olarak indir / yazdır"
                  data-testid={`opt-exam-bulk-docs-${x.id}`}
                >
                  <FileDown size={12} /> Toplu Belge
                </button>
                <button
                  type="button"
                  onClick={() => openClassReport(x.id)}
                  className="text-slate-300 hover:text-indigo-700 p-1 rounded transition-colors shrink-0"
                  title="Sınıf Sonuç Raporu (Yayın Raporu)"
                  data-testid={`opt-exam-report-${x.id}`}
                >
                  <Printer size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => startExamEdit(x)}
                  className="text-slate-300 hover:text-amber-600 p-1 rounded transition-colors shrink-0"
                  title="Sınavı Düzenle / Cevap Anahtarı Gir"
                  data-testid={`opt-exam-edit-${x.id}`}
                >
                  <Edit3 size={16} />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`"${x.name}" sınavı ve tüm optik sonuçları silinecek. Emin misiniz?`)) return;
                    try {
                      await api.delete(`/optical-exams/${x.id}`);
                      toast.success("Sınav silindi");
                      load();
                    } catch (e) {
                      toast.error(apiErr(e));
                    }
                  }}
                  className="text-slate-300 hover:text-rose-600 p-1 rounded transition-colors shrink-0"
                  title="Sınavı Sil"
                  data-testid={`opt-exam-del-${x.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {classReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setClassReport(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="class-report-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">Sınıf Sonuç Raporu — {classReport.exam?.name || ""}</h3>
              <div className="flex items-center gap-2">
                {!classReport.loading && classReport.rows.length > 0 && (
                  <Button size="sm" onClick={printClassReport} className="bg-indigo-900 hover:bg-indigo-800" data-testid="class-report-print">
                    <Printer size={14} />
                    <span className="ml-1">Yazdır / PDF</span>
                  </Button>
                )}
                <button onClick={() => setClassReport(null)} className="text-slate-400 hover:text-slate-700" data-testid="class-report-close">×</button>
              </div>
            </div>
            {classReport.loading ? (
              <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
            ) : classReport.rows.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">Bu sınav için henüz yayınlanmış sonuç yok.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="px-2 py-2 font-medium">Sıra</th>
                    <th className="px-2 py-2 font-medium">Öğrenci</th>
                    <th className="px-2 py-2 font-medium">Alan</th>
                    <th className="px-2 py-2 font-medium text-right">D</th>
                    <th className="px-2 py-2 font-medium text-right">Y</th>
                    <th className="px-2 py-2 font-medium text-right">B</th>
                    <th className="px-2 py-2 font-medium text-right">Net</th>
                    <th className="px-2 py-2 font-medium text-center">Belge</th>
                  </tr>
                </thead>
                <tbody data-testid="class-report-table">
                  {classReport.rows.map((r) => (
                    <tr key={r.student_id} className="border-b border-slate-100">
                      <td className="px-2 py-2 font-bold text-indigo-700">{r.rank}</td>
                      <td className="px-2 py-2 font-medium text-slate-800">{r.name}</td>
                      <td className="px-2 py-2 text-xs text-slate-500">{r.track || "-"}</td>
                      <td className="px-2 py-2 text-right text-emerald-700 font-semibold">{r.totals?.dc ?? 0}</td>
                      <td className="px-2 py-2 text-right text-rose-700 font-semibold">{r.totals?.yc ?? 0}</td>
                      <td className="px-2 py-2 text-right text-slate-500">{r.totals?.blank ?? 0}</td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-indigo-700">{Number(r.totals?.net || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-center">
                        {r.approved ? (
                          <span className="text-[10px] font-bold text-indigo-700">Onaylı</span>
                        ) : (
                          <button onClick={async () => { try { await api.put(`/optical-exams/${classReport.exam.id}/approve`, { student_ids: [r.student_id] }); toast.success(`${r.name} onaylandı`); openClassReport(classReport.exam.id); load(); } catch (e) { toast.error(apiErr(e)); } }}
                            className="text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-0.5 rounded-full" data-testid={`class-report-approve-${r.student_id}`}>Onayla</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
